import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  Radio,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import BulletinSection from '@/components/common/BulletinSection';
import FactsStrip, { type Fact } from '@/components/common/FactsStrip';
import FormErrorsSummary, { type FormErrorItem } from '@/components/common/FormErrorsSummary';
import PageTitle from '@/components/common/PageTitle';
import BasisPicker from '@/components/common/BasisPicker';
import FormMessage from '@/components/auth/FormMessage';
import UploadZone from '@/components/app/company/UploadZone';
import FileList from '@/components/app/sell/form/FileList';
import BuyAcceptanceFields from '@/components/app/buy/BuyAcceptanceFields';
import BuyAttestationGate from '@/components/app/buy/BuyAttestationGate';
import ConsigneeFields, { type ConsigneeType } from '@/components/app/buy/form/ConsigneeFields';
import PriceRows from '@/components/app/buy/form/PriceRows';
import ShiftLine from '@/components/app/buy/form/ShiftLine';
import {
  BLOCKER_SECTION,
  PRICE_MAX,
  PRICE_MIN,
  EMPTY_ROW,
  SECTION_ORDER,
  applyShift,
  buildPriceListPayload,
  draftFromTemplate,
  firstOutOfRange,
  priceCellId,
  priceListBlockers,
  priceListFieldId,
  publishStats,
  qaFieldId,
  rowKeysInOrder,
  toCell,
  type PriceColumn,
  type PriceDraft,
  type PriceListBlocker,
  type PriceListSection,
} from '@/components/app/buy/form/priceListForm';
import { orderService } from '@/api/services/OrderService';
import { BUY_TREE_QUERY_KEY, useBuyTree } from '@/hooks/useBuy';
import { useProfile } from '@/hooks/useProfile';
import { useIsAgent } from '@/hooks/useRoles';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useAuth } from '@/stores/authContext';
import { formatMoney, formatOrgName } from '@/lib/format';
import { focusFieldById } from '@/lib/focusField';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { makeGroupUrl } from '@/lib/buyLinks';
import { flattenBuyTree, groupByBasis } from '@/lib/buyTree';
import { buildQualityAcceptance, isAcceptanceComplete, type QaOverrides } from '@/lib/buyAcceptance';
import { ROUTES } from '@/routes/paths';
import type { AddressOption, CompanyCard, OrderFile } from '@/types/buy';
import classes from './PriceListFormPage.module.css';

const COMMENT_MAX = 4096;

const SECTION_TITLE: Record<PriceListSection, string> = {
  where: 'app.buy.form.sectionWhere',
  prices: 'app.buy.form.prices',
  acceptance: 'app.buy.form.sectionAcceptance',
  consignee: 'app.buy.form.consignee',
  agree: 'app.buy.form.sectionAgree',
};

// Форма создания и правки прайс-листа на покупку («Бюллетень»), по образцу формы предложения о продаже. Строки
// качества приходят от сервера (шаблон onlyExisted=false), покупатель проставляет цены. Года у закупки нет (решение
// 2026-11-04): прайс-лист определяется базисом и культурой. Кнопка отправки доступна всегда: попытка с незаполненным
// показывает сводку со ссылками к полям и переводит фокус на первое из них.
export default function PriceListFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { data: profile } = useProfile();
  const isAgent = useIsAgent();
  const [params] = useSearchParams();

  // Правка — маршрут /app/buy/edit с базисом и культурой из детали; менять их нельзя. Параметр `year` старых ссылок
  // не читается.
  const editAddress = params.get('address');
  const editAddressTitle = params.get('addressTitle');
  const editGoods = params.get('goods');
  const onEditRoute = useMatch(ROUTES.APP_BUY_EDIT) !== null;
  const editMode = onEditRoute && !!(editAddress && editAddressTitle && editGoods);
  // Вход с «+» в списке: базис (и культура) подставлены, но остаются изменяемыми.
  const prefill = !editMode && !!(editAddress && editAddressTitle);

  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(
    editMode || prefill ? { address: editAddress!, title: editAddressTitle! } : null,
  );
  const [goodsType, setGoodsType] = useState<string | null>(editMode || prefill ? editGoods : null);
  // Отмена смены базиса пересоздаёт поле выбора: иначе в нём осталась бы подпись нового базиса при прежнем выборе.
  const [pickerKey, setPickerKey] = useState(0);

  const [prices, setPrices] = useState<PriceDraft>({});
  const [creditType, setCreditType] = useState<string | null>('BY_AVERAGE_VALUE');
  const [byGost, setByGost] = useState(true);
  const [qaOverrides, setQaOverrides] = useState<QaOverrides>({});
  const [consigneeType, setConsigneeType] = useState<ConsigneeType>('buyer');
  const [consignee, setConsignee] = useState<CompanyCard | null>(null);
  const [files, setFiles] = useState<OrderFile[]>([]);
  // Файлы прайс-листа, удалённые в форме: снимаются с сервера только после сохранения, чтобы уход без сохранения не
  // оставил опубликованный прайс-лист без особых условий.
  const [removedFileIds, setRemovedFileIds] = useState<number[]>([]);
  const [comment, setComment] = useState('');
  const [agree, setAgree] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const templateErrorRef = useRef<HTMLDivElement>(null);

  // Несохранённые изменения — для предупреждения об уходе. Ставится в обработчиках ввода, не при заполнении из шаблона.
  const dirtyRef = useRef(false);
  // Введено ли что-то, кроме базиса и культуры: только тогда их смена спрашивает подтверждение.
  const enteredRef = useRef(false);
  // Файлы, загруженные в этой форме: у них ещё нет владельца, поэтому удаляются с сервера сразу.
  const sessionUploadsRef = useRef<Set<number>>(new Set());
  const markEntered = useCallback(() => {
    dirtyRef.current = true;
    enteredRef.current = true;
  }, []);

  const goodsTypes = useQuery({
    queryKey: ['parentGoodsTypes'] as const,
    queryFn: ({ signal }) => orderService.getParentGoodsTypes(signal),
    enabled: isAuthenticated && !editMode,
    staleTime: 10 * 60_000,
  });

  // Мои прайс-листы: базисы для быстрого выбора и признак «прайс-лист уже есть».
  const buyTree = useBuyTree();
  const myGroups = useMemo(() => flattenBuyTree(buyTree.data), [buyTree.data]);
  const ownBases = useMemo(
    () =>
      groupByBasis(myGroups).map((base) => ({
        address: base.address,
        addressTitle: base.addressTitle,
        basisId: base.items[0]?.basisId,
      })),
    [myGroups],
  );

  const templateKey = selectedAddress && goodsType ? `${selectedAddress.address}|${selectedAddress.title ?? ''}|${goodsType}` : null;
  const template = useQuery({
    queryKey: ['priceListTemplate', selectedAddress?.address, selectedAddress?.title, goodsType] as const,
    queryFn: ({ signal }) =>
      orderService.getPurchasePriceList(
        {
          address: selectedAddress!.address,
          addressTitle: selectedAddress!.title ?? '',
          parentGoodsType: goodsType!,
          onlyExisted: false,
        },
        signal,
      ),
    enabled: isAuthenticated && templateKey !== null,
    // Строки качества читаются при открытии формы и явно после ответа 412. Фоновое перечитывание (возврат сети)
    // подменило бы версии строк под введёнными ценами, и сохранение обошло бы проверку версии сервером.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const qaDefaults = useQuery({
    queryKey: ['qaDefaults', goodsType] as const,
    queryFn: ({ signal }) => orderService.getQaDefaults(goodsType!, signal),
    enabled: isAuthenticated && !!goodsType,
    staleTime: 5 * 60_000,
  });

  const defaultConsignee = useQuery({
    queryKey: ['defaultConsignee', selectedAddress?.address, selectedAddress?.title] as const,
    queryFn: ({ signal }) => orderService.getDefaultConsignee(selectedAddress!.address, selectedAddress!.title ?? '', signal),
    enabled: isAuthenticated && !!selectedAddress,
    staleTime: 5 * 60_000,
  });

  // Заполнение формы из шаблона — один раз на выбранные базис и культуру. Повторный ответ сервера (возврат сети,
  // повторное чтение) введённое не затирает. После ответа «прайс-лист изменился» поколение заполнения увеличивается
  // явно: пришедшие данные могут совпасть с прежними, и тогда React Query сохранит прежний объект, а эффекты по
  // одним данным не сработали бы.
  const [initGeneration, setInitGeneration] = useState(0);
  const initToken = templateKey ? `${templateKey}#${initGeneration}` : null;
  const pricesInitRef = useRef<string | null>(null);
  const qaInitRef = useRef<string | null>(null);
  const consigneeInitRef = useRef<string | null>(null);
  useEffect(() => {
    const data = template.data;
    if (!data || !initToken || pricesInitRef.current === initToken) {
      return;
    }
    pricesInitRef.current = initToken;
    setPrices(draftFromTemplate(data));
    setCreditType(data.qualityCreditType ?? 'BY_AVERAGE_VALUE');
    setByGost(data.qualityAcceptanceByGost ?? true);
    setComment(data.userComment ?? '');
    // Существующие особые условия возвращаются в specialTermsFileIds: иначе правка цены стёрла бы прикреплённые файлы.
    // Файлы, загруженные в этой форме, остаются в списке: после ответа 412 они иначе пропали бы и остались без владельца.
    const saved = (data.specialTermsFiles ?? []).map((file) => ({ id: file.id, name: file.name }));
    setFiles((current) => [
      ...saved,
      ...current.filter((file) => sessionUploadsRef.current.has(file.id) && !saved.some((item) => item.id === file.id)),
    ]);
    setRemovedFileIds([]);
  }, [template.data, initToken]);

  // Надбавки и скидки приёмки: сохранённые значения прайс-листа (не по ГОСТу) поверх значений справочника.
  useEffect(() => {
    const defaults = qaDefaults.data;
    const data = template.data;
    if (!defaults || !data || !initToken || qaInitRef.current === initToken) {
      return;
    }
    qaInitRef.current = initToken;
    const saved = data.qualityAcceptanceByGost === false ? (data.qualityAcceptanceProperties ?? []) : [];
    const next: QaOverrides = {};
    for (const property of defaults) {
      const stored = saved.find((item) => item.name === property.name);
      next[property.name] = {
        inc: stored?.increaseValue ?? property.increaseValue ?? '',
        dec: stored?.decreaseWeightValue ?? property.decreaseWeightValue ?? '',
      };
    }
    setQaOverrides(next);
  }, [qaDefaults.data, template.data, initToken]);

  // Грузополучатель сохранённого прайс-листа. Сервер не сообщает его тип, поэтому тип определяется сравнением ИНН с
  // грузополучателем по умолчанию. Ждём ответа о грузополучателе по умолчанию (или его сбоя) и не трогаем выбор,
  // который пользователь уже сделал сам: поздний ответ не должен его отменить.
  const consigneeTouchedRef = useRef(false);
  useEffect(() => {
    const data = template.data;
    const defaultSettled = defaultConsignee.isSuccess || defaultConsignee.isError;
    if (!data || !defaultSettled || !initToken || consigneeInitRef.current === initToken) {
      return;
    }
    consigneeInitRef.current = initToken;
    if (consigneeTouchedRef.current) {
      return;
    }
    if (!data.consigneeInn || data.consigneeInn === defaultConsignee.data?.consigneeInn) {
      setConsigneeType('buyer');
      setConsignee(null);
      return;
    }
    setConsigneeType('other');
    setConsignee({
      name: data.consigneeName,
      inn: data.consigneeInn,
      ogrn: data.consigneeOgrn,
      kpp: data.consigneeKpp,
      okpo: data.consigneeOkpo,
      address: data.consigneeAddress,
    });
  }, [template.data, defaultConsignee.data, defaultConsignee.isSuccess, defaultConsignee.isError, initToken]);

  // Поколение выбора базиса и культуры: загрузка, начатая для прежнего прайс-листа, к новому не прикрепляется.
  const contextGenerationRef = useRef(0);
  const upload = useMutation({
    mutationFn: ({ selected }: { readonly selected: File[]; readonly generation: number }) =>
      orderService.uploadPurchaseSpecialTerms(selected),
    onSuccess: (uploaded, { generation }) => {
      if (generation !== contextGenerationRef.current) {
        for (const file of uploaded) {
          void orderService.deletePurchaseFile(file.id).catch(() => undefined);
        }
        return;
      }
      markEntered();
      for (const file of uploaded) {
        sessionUploadsRef.current.add(file.id);
      }
      setFiles((current) => [...current, ...uploaded]);
    },
    onError: (error) => notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  const removeUpload = useMutation({
    mutationFn: (fileId: number) => orderService.deletePurchaseFile(fileId),
    onSuccess: (_, fileId) => {
      sessionUploadsRef.current.delete(fileId);
      setFiles((current) => current.filter((file) => file.id !== fileId));
    },
    onError: (error) => notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  // Уход без сохранения: загруженные в форме файлы так и остались бы на сервере без владельца.
  useEffect(
    () => () => {
      for (const fileId of sessionUploadsRef.current) {
        void orderService.deletePurchaseFile(fileId).catch(() => undefined);
      }
    },
    [],
  );

  const downloadFile = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: (error) => notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  /** Убрать файл: загруженный в этой форме — сразу с сервера, файл прайс-листа — из списка, с сервера после сохранения. */
  function removeFile(fileId: number): void {
    markEntered();
    if (sessionUploadsRef.current.has(fileId)) {
      removeUpload.mutate(fileId);
      return;
    }
    setFiles((current) => current.filter((file) => file.id !== fileId));
    setRemovedFileIds((ids) => [...ids, fileId]);
  }

  const onPriceChange = useCallback(
    (key: string, column: PriceColumn, value: number | string) => {
      markEntered();
      setPrices((current) => ({ ...current, [key]: { ...(current[key] ?? EMPTY_ROW), [column]: toCell(value) } }));
    },
    [markEntered],
  );

  // ── Что мешает отправке ─────────────────────────────────────────────────────
  const stats = useMemo(() => publishStats(prices), [prices]);
  const qaProps = qaDefaults.data ?? [];
  const blockers = priceListBlockers({
    hasAddress: !!selectedAddress,
    hasGoods: !!goodsType,
    stats,
    hasCredit: !!creditType,
    // Пока значения приёмки не загружены, правила неизвестны: отправка сохранила бы пустой список и стёрла их.
    qaComplete: qaDefaults.isSuccess && isAcceptanceComplete(byGost, qaOverrides, qaProps),
    // Грузополучатель по умолчанию передаётся своим ИНН: без него сервер получил бы пустое значение.
    consigneeChosen: consigneeType === 'other' ? !!consignee?.inn : !!defaultConsignee.data?.consigneeInn,
    agree,
  })
    // Пока строки качества не загружены, остальных разделов на экране нет — и ссылаться на них сводке нечем.
    .filter((blocker) => template.data || BLOCKER_SECTION[blocker] === 'where');
  const canSubmit = !!template.data && blockers.length === 0;
  const remainingSections = SECTION_ORDER.filter((section) => blockers.some((blocker) => BLOCKER_SECTION[blocker] === section));

  /** Элемент, к которому ведёт пункт сводки. */
  function blockerTarget(blocker: PriceListBlocker): string {
    const order = template.data ? rowKeysInOrder(template.data) : [];
    switch (blocker) {
      case 'needPrice':
        return order[0] ? priceCellId(order[0], 'p0') : priceListFieldId('prices');
      case 'outOfRange': {
        const first = firstOutOfRange(prices, order);
        return first ? priceCellId(first.key, first.column) : priceListFieldId('prices');
      }
      case 'consignee':
        return priceListFieldId(consigneeType === 'other' ? 'consigneeSearch' : 'consignee');
      case 'qa': {
        // Ведём к первому пустому правилу; пока значения приёмки не загружены — к разделу с сообщением.
        const empty = byGost ? undefined : qaProps.find((property) => qaOverrides[property.name]?.inc === '' || qaOverrides[property.name]?.dec === '');
        if (!empty) {
          return priceListFieldId('qa');
        }
        return qaFieldId(empty.name, qaOverrides[empty.name]?.inc === '' ? 'inc' : 'dec');
      }
      default:
        return priceListFieldId(blocker);
    }
  }

  function blockerLabel(blocker: PriceListBlocker): string {
    switch (blocker) {
      case 'address':
        return t('app.buy.form.address');
      case 'goods':
        return t('app.buy.form.goods');
      case 'needPrice':
        return t('app.buy.form.needPrice');
      case 'outOfRange':
        return t('app.buy.form.outOfRange', { count: stats.invalid });
      case 'credit':
        return t('app.buy.terms.credit');
      case 'qa':
        return t('app.buy.form.needQa');
      case 'consignee':
        return t('app.buy.form.needConsignee');
      case 'agree':
        return t('app.buy.form.needAgree');
    }
  }

  // Смена базиса или культуры заново заполняет форму из строк качества другого прайс-листа. Если введено хоть что-то,
  // сначала спрашиваем: молча данные не теряем.
  function confirmContextChange(apply: () => void, onCancel?: () => void): void {
    const run = (): void => {
      // Загруженные в этой форме файлы к новому прайс-листу не относятся: снимаем их с сервера, не дожидаясь ответа.
      for (const fileId of sessionUploadsRef.current) {
        void orderService.deletePurchaseFile(fileId).catch(() => undefined);
      }
      sessionUploadsRef.current = new Set();
      contextGenerationRef.current += 1;
      // Форма заполнится заново из строк качества выбранного прайс-листа, даже если выбор вернулся к прежнему.
      setInitGeneration((generation) => generation + 1);
      enteredRef.current = false;
      setAttempted(false);
      setSubmitError(null);
      setPrices({});
      setFiles([]);
      // Согласие дано на другой прайс-лист (у другого базиса и культуры может быть другая комиссия).
      setAgree(false);
      consigneeTouchedRef.current = false;
      apply();
    };
    if (!enteredRef.current) {
      run();
      return;
    }
    // Окно закрывают не только кнопкой «Отмена», но и Esc, щелчком мимо и крестиком: любое закрытие без подтверждения —
    // отмена смены.
    let confirmed = false;
    modals.openConfirmModal({
      title: t('app.buy.form.resetTitle'),
      children: <Text size="sm">{t('app.buy.form.resetText')}</Text>,
      labels: { confirm: t('app.buy.form.resetConfirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        confirmed = true;
        run();
      },
      onClose: () => {
        if (!confirmed) {
          onCancel?.();
        }
      },
    });
  }

  useUnsavedChangesGuard(() => dirtyRef.current);

  const submit = useMutation({
    mutationFn: async () => {
      await orderService.editPriceList(
        buildPriceListPayload({
          template: template.data!,
          draft: prices,
          basisId: selectedAddress?.basisId,
          agree,
          creditType: creditType!,
          byGost,
          qaProperties: buildQualityAcceptance(byGost, qaOverrides, qaProps).qaProperties,
          fileIds: files.map((file) => file.id),
          comment,
          // Сервер принимает только ИНН; для грузополучателя по умолчанию передаётся его ИНН.
          consigneeInn: consigneeType === 'other' ? consignee?.inn : defaultConsignee.data?.consigneeInn,
        }),
      );
      // Удалённые файлы уже не входят в прайс-лист; сбой их удаления результат не меняет.
      await Promise.allSettled(removedFileIds.map((fileId) => orderService.deletePurchaseFile(fileId)));
    },
    onSuccess: () => {
      dirtyRef.current = false;
      // Загруженные файлы теперь принадлежат прайс-листу: при уходе со страницы их удалять нельзя.
      sessionUploadsRef.current = new Set();
      for (const queryKey of [BUY_TREE_QUERY_KEY, ['purchasePriceList'], ['buyBestPrices'], ['bestOffers']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      notifications.show({ color: 'lime', message: t('app.buy.form.saved') });
      navigate(makeGroupUrl({ address: template.data!.address, addressTitle: template.data!.addressTitle, goods: goodsType! }));
    },
    onError: (error) => {
      const status = getStatus(error);
      if (status === 423) {
        // Уход к аттестации намеренный: окно «Уйти без сохранения?» поверх уведомления не нужно.
        dirtyRef.current = false;
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      if (status === 406) {
        if (!editMode) {
          setAddressError(t('app.buy.form.errAddress'));
        }
        setSubmitError(t('app.buy.form.errAddress'));
        return;
      }
      if (status === 412) {
        // Прайс-лист изменился на сервере: форма заполняется заново по сохранённым данным, и об этом сказано прямо.
        // Если перечитать не удалось, введённое не сбрасывается: старые данные заменили бы его без пользы.
        void template.refetch().then((result) => {
          if (result.isSuccess) {
            consigneeTouchedRef.current = false;
            setInitGeneration((generation) => generation + 1);
            setSubmitError(t('app.buy.form.changedReloaded'));
          } else {
            setSubmitError(t('app.buy.form.changedReloadFailed'));
          }
        });
        return;
      }
      setSubmitError(isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')));
    },
  });

  // Кнопка с индикатором загрузки теряет фокус, поэтому ошибку сохранения показываем и переводим на неё фокус.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.focus();
    }
  }, [submitError]);

  function handleSubmit(): void {
    setSubmitError(null);
    if (!canSubmit) {
      setAttempted(true);
      const first = blockers[0];
      if (first) {
        const target = blockerTarget(first);
        requestAnimationFrame(() => focusFieldById(target));
      } else {
        // Мешает не поле, а незагруженные строки качества: ведём к сообщению о сбое с кнопкой повтора.
        templateErrorRef.current?.focus();
      }
      return;
    }
    submit.mutate();
  }

  // Агент не публикует прайс-листы (он посредник, а не сторона сделки) — и по прямой ссылке тоже.
  if (isAgent) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }

  // Без аттестации сервер ответит 423 — говорим об этом сразу.
  if (profile && profile.isVerified === false) {
    return (
      <Container size="md" py="xl">
        <BuyAttestationGate />
      </Container>
    );
  }

  // Словарь культур грузится асинхронно: подставленное значение добавляем сразу.
  const goodsData = goodsType && !(goodsTypes.data ?? []).includes(goodsType) ? [goodsType, ...(goodsTypes.data ?? [])] : (goodsTypes.data ?? []);

  const backUrl = editMode ? makeGroupUrl({ address: editAddress!, addressTitle: editAddressTitle!, goods: editGoods! }) : ROUTES.APP_BUY;
  const backLabel = editMode ? t('app.buy.offer.back') : t('app.buy.back');

  // Прайс-лист на эти базис и культуру уже есть: создание обновит его, а не создаст второй.
  const existing =
    !editMode && selectedAddress && goodsType
      ? myGroups.find(
          (group) => group.address === selectedAddress.address && group.addressTitle === (selectedAddress.title ?? '') && group.goods === goodsType,
        )
      : undefined;

  const facts: Fact[] = editMode
    ? [
        {
          key: 'basis',
          label: t('app.buy.form.address'),
          value: formatOrgName(editAddressTitle) || editAddress,
          note: editAddressTitle ? editAddress : undefined,
          wide: true,
        },
        { key: 'goods', label: t('app.buy.form.goods'), value: editGoods },
        ...(template.data?.updatedDate ? [{ key: 'updated', label: t('app.buy.table.updated'), value: template.data.updatedDate }] : []),
      ]
    : [];

  const summaryItems: FormErrorItem[] = blockers.map((blocker) => ({
    label: blockerLabel(blocker),
    onSelect: () => focusFieldById(blockerTarget(blocker)),
  }));

  const contextReady = !!selectedAddress && !!goodsType;

  return (
    <Container size="md" py="xl">
      <Stack gap={40}>
        <Stack gap={6}>
          <Anchor component={Link} to={backUrl} size="sm" c="brand.7" w="fit-content">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} aria-hidden />
              {backLabel}
            </Group>
          </Anchor>
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {t(editMode ? 'app.buy.form.titleEdit' : 'app.buy.form.titleNew')}
          </PageTitle>
          {editMode ? (
            <FactsStrip facts={facts} />
          ) : (
            <Text c="gray.7" size="sm">
              {t('app.buy.form.immutableWarning')}
            </Text>
          )}
        </Stack>

        {editMode ? null : (
          <BulletinSection title={t(SECTION_TITLE.where)} spaced>
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <BasisPicker
                  key={pickerKey}
                  id={priceListFieldId('address')}
                  required
                  label={t('app.buy.form.address')}
                  placeholder={t('app.buy.form.addressPlaceholder')}
                  description={t('app.buy.form.addressHint')}
                  ownBases={ownBases}
                  value={
                    selectedAddress
                      ? { address: selectedAddress.address, addressTitle: selectedAddress.title ?? '', basisId: selectedAddress.basisId }
                      : null
                  }
                  onChange={(picked) => {
                    // Повторный выбор того же базиса ничего не меняет и не сбрасывает введённое.
                    if (picked?.address === selectedAddress?.address && (picked?.addressTitle || undefined) === selectedAddress?.title) {
                      return;
                    }
                    confirmContextChange(
                      () => {
                        dirtyRef.current = true;
                        setAddressError(null);
                        setSelectedAddress(
                          picked ? { address: picked.address, title: picked.addressTitle || undefined, basisId: picked.basisId } : null,
                        );
                      },
                      () => setPickerKey((key) => key + 1),
                    );
                  }}
                  error={addressError ?? (attempted && !selectedAddress ? t('auth.validation.required') : undefined)}
                />
                <Select
                  id={priceListFieldId('goods')}
                  required
                  label={t('app.buy.form.goods')}
                  placeholder={t('app.buy.form.goodsPlaceholder')}
                  data={goodsData}
                  value={goodsType}
                  onChange={(value) => {
                    if (value !== goodsType) {
                      confirmContextChange(() => {
                        dirtyRef.current = true;
                        setGoodsType(value);
                      });
                    }
                  }}
                  searchable
                  error={attempted && !goodsType ? t('auth.validation.required') : undefined}
                />
              </SimpleGrid>
              {existing ? (
                <FormMessage tone="info" quiet>
                  <span>{t('app.buy.form.existing')} </span>
                  <Anchor component={Link} to={makeGroupUrl(existing)} size="sm" c="brand.7">
                    {t('app.buy.form.openExisting')}
                  </Anchor>
                </FormMessage>
              ) : null}
            </Stack>
          </BulletinSection>
        )}

        {/* Сбой повторного чтения при уже показанных строках форму не прячет: ввод остаётся на экране. */}
        {contextReady && template.isError && !template.data ? (
          <FormMessage ref={templateErrorRef}>
            <span>{isServiceUnavailable(template.error) ? t('auth.errors.unavailable') : t('app.buy.form.templateError')}</span>
            <Button variant="subtle" size="compact-sm" onClick={() => void template.refetch()}>
              {t('common.retry')}
            </Button>
          </FormMessage>
        ) : contextReady && template.isLoading ? (
          <Stack gap="md">
            <Skeleton h={28} w={200} />
            <Skeleton h={220} />
          </Stack>
        ) : template.data ? (
          <>
            <BulletinSection
              title={t(SECTION_TITLE.prices)}
              aside={
                <span className={classes.counter} id={priceListFieldId('prices')}>
                  {t('app.buy.form.willPublish', { count: stats.filled, total: stats.total })}
                </span>
              }
              spaced
            >
              <Stack gap="md">
                <Text size="sm" c="gray.7">
                  {t('app.buy.form.pricesHint', { min: formatMoney(PRICE_MIN), max: formatMoney(PRICE_MAX) })}
                </Text>
                <ShiftLine
                  draft={prices}
                  onApply={(step) => {
                    markEntered();
                    setPrices((current) => applyShift(current, step));
                  }}
                />
                <PriceRows list={template.data} draft={prices} onChange={onPriceChange} attempted={attempted} />
                {attempted && stats.filled === 0 ? (
                  <Text size="sm" c="loss.8">
                    {t('app.buy.form.needPrice')}
                  </Text>
                ) : null}
              </Stack>
            </BulletinSection>

            <BulletinSection title={t(SECTION_TITLE.acceptance)} spaced>
              <Stack gap="lg">
                <Radio.Group
                  id={priceListFieldId('credit')}
                  label={t('app.buy.terms.credit')}
                  value={creditType}
                  onChange={(value) => {
                    markEntered();
                    setCreditType(value);
                  }}
                  error={attempted && !creditType ? t('auth.validation.required') : undefined}
                >
                  <Stack gap="xs" mt={6}>
                    <Radio value="BY_AVERAGE_VALUE" label={t('app.buy.terms.creditAverage')} />
                    <Radio value="BY_EACH_DAY" label={t('app.buy.terms.creditEachDay')} />
                  </Stack>
                </Radio.Group>
                <div id={priceListFieldId('qa')} tabIndex={-1} className={classes.target}>
                  {qaDefaults.isError ? (
                    <FormMessage>
                      <span>{t('app.buy.form.qaError')}</span>
                      <Button variant="subtle" size="compact-sm" onClick={() => void qaDefaults.refetch()}>
                        {t('common.retry')}
                      </Button>
                    </FormMessage>
                  ) : null}
                  <BuyAcceptanceFields
                    qaProps={qaProps}
                    byGost={byGost}
                    onByGost={(value) => {
                      markEntered();
                      setByGost(value);
                    }}
                    overrides={qaOverrides}
                    onOverride={(name, field, value) => {
                      markEntered();
                      setQaOverrides((current) => ({ ...current, [name]: { ...(current[name] ?? { inc: '', dec: '' }), [field]: value } }));
                    }}
                    loading={qaDefaults.isLoading}
                    noProps={qaDefaults.isSuccess && qaProps.length === 0}
                    showEmptyErrors={attempted}
                    fieldId={qaFieldId}
                  />
                </div>
              </Stack>
            </BulletinSection>

            <BulletinSection title={t(SECTION_TITLE.consignee)} spaced>
              <Stack gap="md">
                <Text size="sm" c="gray.7">
                  {t('app.buy.form.consigneeWarning')}
                </Text>
                {defaultConsignee.isError ? (
                  <FormMessage>
                    <span>{t('app.buy.form.consigneeDefaultError')}</span>
                    <Button variant="subtle" size="compact-sm" onClick={() => void defaultConsignee.refetch()}>
                      {t('common.retry')}
                    </Button>
                  </FormMessage>
                ) : null}
                <ConsigneeFields
                  defaultConsignee={defaultConsignee.data}
                  type={consigneeType}
                  consignee={consignee}
                  onType={(type) => {
                    markEntered();
                    consigneeTouchedRef.current = true;
                    setConsigneeType(type);
                  }}
                  onConsignee={(company) => {
                    markEntered();
                    consigneeTouchedRef.current = true;
                    setConsignee(company);
                  }}
                  attempted={attempted}
                />
              </Stack>
            </BulletinSection>

            <BulletinSection title={t('app.buy.form.sectionExtraOptional')} spaced>
              <Stack gap="lg">
                <Stack gap="sm">
                  <Text fw={500} size="sm">
                    {t('app.buy.offer.specialTerms')}
                  </Text>
                  <FileList files={files} onRemove={removeFile} onDownload={(id) => downloadFile.mutate(id)} removing={removeUpload} />
                  <UploadZone
                    multiple
                    accept=".pdf"
                    hint={t('app.buy.form.filesHint')}
                    loading={upload.isPending}
                    onFiles={(selected) => upload.mutate({ selected, generation: contextGenerationRef.current })}
                  />
                </Stack>
                <Textarea
                  label={t('app.buy.terms.comment')}
                  placeholder={t('app.buy.form.commentPlaceholder')}
                  maxLength={COMMENT_MAX}
                  autosize
                  minRows={2}
                  maxRows={6}
                  value={comment}
                  onChange={(event) => {
                    markEntered();
                    setComment(event.currentTarget.value);
                  }}
                />
              </Stack>
            </BulletinSection>

            <BulletinSection title={t(SECTION_TITLE.agree)} spaced>
              <Stack gap="sm">
                {template.data.reward ? <Text>{t('app.buy.form.commission', { value: formatMoney(template.data.reward) })}</Text> : null}
                <Checkbox
                  id={priceListFieldId('agree')}
                  checked={agree}
                  onChange={(event) => {
                    markEntered();
                    setAgree(event.currentTarget.checked);
                  }}
                  label={t('app.buy.offer.agreeOferta')}
                  error={attempted && !agree ? t('auth.validation.acceptRequired') : undefined}
                />
              </Stack>
            </BulletinSection>
          </>
        ) : null}

        <Stack gap="md">
          {submitError ? <FormMessage ref={submitErrorRef}>{submitError}</FormMessage> : null}
          {/* До попытки — спокойная строка «Осталось заполнить», после неё — сводка со ссылками к полям. */}
          {attempted ? (
            <FormErrorsSummary title={t('common.fixFormErrors')} items={summaryItems} />
          ) : remainingSections.length > 0 ? (
            <Text size="sm" c="gray.7">
              {t('app.buy.form.remaining')} {remainingSections.map((section) => t(SECTION_TITLE[section])).join(' · ')}
            </Text>
          ) : null}
          <Group justify="flex-end">
            <Button size="md" onClick={handleSubmit} loading={submit.isPending || (contextReady && template.isLoading)}>
              {t(editMode ? 'app.buy.form.saveEdit' : 'app.buy.form.save')}
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Container>
  );
}
